// BUILD_TEST_12345
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, AlertTriangle, User, Building2, Calendar, Euro, CreditCard,
  FileText, Upload, Download, Trash2, CheckCircle, Clock, Circle,
  FileCheck, Send, MessageSquare, FolderOpen, Plus, Eye, ChevronDown,
  ChevronUp, AlertCircle, History, Settings, Edit, Save, X, Image,
  File, FileSpreadsheet, Loader2, Phone, Mail, MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { estafasBancariasAPI, contactosAPI } from '../api';
import { toast } from 'sonner';

// Configuración de pasos
const PASOS_CONFIG = [
  { 
    id: 'denuncia_policial', 
    nombre: 'A) Denuncia Policial', 
    descripcion: 'Denuncia ante la policía y documentación del fraude',
    tiposDoc: ['denuncia', 'justificante_bancario'],
    icon: FileText,
    letra: 'A'
  },
  { 
    id: 'reclamacion_inicial_banco', 
    nombre: 'B) Reclamación Inicial Banco', 
    descripcion: 'Solicitud de bloqueo y devolución inicial',
    tiposDoc: ['reclamacion_inicial'],
    icon: Building2,
    letra: 'B'
  },
  { 
    id: 'contrato_bancario', 
    nombre: 'C) Contrato Bancario', 
    descripcion: 'Copia del contrato con el banco',
    tiposDoc: ['contrato_banco'],
    icon: FileCheck,
    letra: 'C'
  },
  { 
    id: 'reclamacion_extrajudicial', 
    nombre: 'D) Reclamación Extrajudicial', 
    descripcion: 'Reclamación formal al banco (generada)',
    tiposDoc: ['reclamacion_extrajudicial'],
    generado: true,
    tipoGeneracion: 'reclamacion',
    icon: FileText,
    letra: 'D'
  },
  { 
    id: 'hoja_encargo', 
    nombre: 'E) Hoja de Encargo', 
    descripcion: 'Contrato de servicios con el cliente',
    tiposDoc: ['hoja_encargo'],
    generado: true,
    tipoGeneracion: 'hoja_encargo',
    icon: FileText,
    letra: 'E'
  },
  { 
    id: 'informe_resumen', 
    nombre: 'F) Informe Resumen', 
    descripcion: 'Resumen de la reclamación',
    tiposDoc: ['informe_resumen'],
    generado: true,
    tipoGeneracion: 'informe',
    icon: FileText,
    letra: 'F'
  },
  { 
    id: 'envio_banco', 
    nombre: 'G) Envío al Banco', 
    descripcion: 'Envío de reclamación y documento adjunto',
    tiposDoc: ['envio_certificado', 'respuesta_banco'],
    icon: Send,
    letra: 'G'
  },
  { 
    id: 'crear_expediente', 
    nombre: 'H) Crear Expediente', 
    descripcion: 'Conversión a expediente judicial',
    tiposDoc: [],
    icon: FolderOpen,
    letra: 'H'
  },
];

const TIPOS_ESTAFA = [
  { id: 'phishing', nombre: 'Phishing (correo fraudulento)' },
  { id: 'vishing', nombre: 'Vishing (llamada fraudulenta)' },
  { id: 'smishing', nombre: 'Smishing (SMS fraudulento)' },
  { id: 'sim_swapping', nombre: 'SIM Swapping (duplicado de SIM)' },
  { id: 'pharming', nombre: 'Pharming (web falsa)' },
  { id: 'man_in_the_middle', nombre: 'Man in the middle (interceptación)' },
  { id: 'robo_tarjeta', nombre: 'Robo/clonación de tarjeta' },
  { id: 'acceso_no_autorizado', nombre: 'Acceso no autorizado a banca online' },
  { id: 'otro', nombre: 'Otro (especificar)' },
];

const TIPOS_DOCUMENTO = [
  { id: 'denuncia', nombre: 'Denuncia Policial' },
  { id: 'justificante_bancario', nombre: 'Justificante Bancario' },
  { id: 'reclamacion_inicial', nombre: 'Reclamación Inicial Banco' },
  { id: 'contrato_banco', nombre: 'Contrato Bancario' },
  { id: 'reclamacion_extrajudicial', nombre: 'Reclamación Extrajudicial' },
  { id: 'hoja_encargo', nombre: 'Hoja de Encargo' },
  { id: 'informe_resumen', nombre: 'Informe Resumen' },
  { id: 'envio_certificado', nombre: 'Envío Certificado' },
  { id: 'respuesta_banco', nombre: 'Respuesta del Banco' },
  { id: 'otro', nombre: 'Otro Documento' },
];

// Componente para vista previa de documentos
const DocumentPreview = ({ documento, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);

  const extension = documento.extension?.toLowerCase() || documento.nombre_archivo?.split('.').pop()?.toLowerCase();
  const isImage = ['.jpg', '.jpeg', '.png', '.gif', '.webp', 'jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension);
  const isPdf = ['.pdf', 'pdf'].includes(extension);

  useEffect(() => {
    const loadPreview = async () => {
      try {
        setLoading(true);
        const response = await estafasBancariasAPI.downloadDocumento(documento.caso_id, documento.id);
        const blob = new Blob([response.data], { 
          type: isPdf ? 'application/pdf' : isImage ? `image/${extension.replace('.', '')}` : 'application/octet-stream'
        });
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      } catch (err) {
        console.error('Error loading preview:', err);
        setError('No se pudo cargar la vista previa');
      } finally {
        setLoading(false);
      }
    };

    if (isImage || isPdf) {
      loadPreview();
    } else {
      setLoading(false);
    }

    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [documento]);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            {documento.nombre_original || documento.nombre_archivo}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-auto min-h-[400px]">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <AlertCircle className="w-12 h-12 mb-4" />
              <p>{error}</p>
            </div>
          ) : isImage ? (
            <img src={previewUrl} alt={documento.nombre_original} className="max-w-full h-auto mx-auto" />
          ) : isPdf ? (
            <iframe src={previewUrl} className="w-full h-[600px]" title="PDF Preview" />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <File className="w-16 h-16 mb-4" />
              <p>Vista previa no disponible para este tipo de archivo</p>
              <p className="text-sm mt-2">Extensión: {extension}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Componente para la barra de progreso
const WorkflowProgressBar = ({ caso }) => {
  const calculateProgress = () => {
    const pasos = caso?.pasos_completados || {};
    let completados = 0;
    const totalPasos = PASOS_CONFIG.length;

    PASOS_CONFIG.forEach(paso => {
      if (paso.id === 'envio_banco' && caso?.fecha_envio_banco) {
        completados++;
      } else if (paso.id === 'crear_expediente' && caso?.expediente_id) {
        completados++;
      } else if (pasos[paso.id]?.completado) {
        completados++;
      }
    });

    return Math.round((completados / totalPasos) * 100);
  };

  const progress = calculateProgress();

  const isPasoCompletado = (pasoId) => {
    if (pasoId === 'envio_banco') return !!caso?.fecha_envio_banco;
    if (pasoId === 'crear_expediente') return !!caso?.expediente_id;
    return caso?.pasos_completados?.[pasoId]?.completado === true;
  };

  return (
    <Card className="mb-6">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-gray-600">Progreso del Flujo de Trabajo</span>
          <span className="text-lg font-bold text-blue-600">{progress}%</span>
        </div>
        <Progress value={progress} className="h-3 mb-4" />
        <div className="flex justify-between">
          {PASOS_CONFIG.map((paso) => {
            const completado = isPasoCompletado(paso.id);
            return (
              <div 
                key={paso.id} 
                className="flex flex-col items-center"
                title={paso.nombre}
              >
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                  transition-all duration-300
                  ${completado 
                    ? 'bg-green-500 text-white shadow-md' 
                    : 'bg-gray-200 text-gray-500'
                  }
                `}>
                  {completado ? <CheckCircle className="w-4 h-4" /> : paso.letra}
                </div>
                <span className={`text-xs mt-1 ${completado ? 'text-green-600 font-medium' : 'text-gray-400'}`}>
                  {paso.letra}
                </span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const EstafaBancariaDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [caso, setCaso] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('flujo');
  
  // Estados para edición
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [searchBanco, setSearchBanco] = useState('');
  const [bancos, setBancos] = useState([]);
  
  // Estados para modales
  const [showUploadDoc, setShowUploadDoc] = useState(false);
  const [uploadPaso, setUploadPaso] = useState(null);
  const [uploadTipo, setUploadTipo] = useState('');
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadDescripcion, setUploadDescripcion] = useState('');
  const [uploading, setUploading] = useState(false);
  
  const [showEnvio, setShowEnvio] = useState(false);
  const [envioData, setEnvioData] = useState({ fecha: '', medio: '' });
  
  const [showRespuesta, setShowRespuesta] = useState(false);
  const [respuestaData, setRespuestaData] = useState({ 
    fecha: '', favorable: false, importe: '', observaciones: '' 
  });
  
  const [showCrearExpediente, setShowCrearExpediente] = useState(false);
  const [generandoDoc, setGenerandoDoc] = useState(null);
  
  // Vista previa
  const [previewDoc, setPreviewDoc] = useState(null);

  const loadCaso = useCallback(async () => {
    try {
      setLoading(true);
      const res = await estafasBancariasAPI.getById(id);
      setCaso(res.data);
      setEditData(res.data);
    } catch (error) {
      console.error('Error cargando caso:', error);
      toast.error('Error al cargar el caso');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadCaso();
  }, [loadCaso]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const isPasoCompletado = (pasoId) => {
    return caso?.pasos_completados?.[pasoId]?.completado === true;
  };

  const getDocumentosPaso = (pasoId) => {
    const pasoConfig = PASOS_CONFIG.find(p => p.id === pasoId);
    if (!pasoConfig || !caso?.documentos) return [];
    return caso.documentos.filter(d => 
      pasoConfig.tiposDoc.includes(d.tipo) || d.paso === pasoId
    );
  };

  // Buscar bancos para el formulario de edición
  const searchBancos = async (query) => {
    if (query.length < 2) return;
    try {
      const res = await contactosAPI.getAll({ search: query, limit: 20 });
      const data = res.data.contactos || res.data || [];
      setBancos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error buscando bancos:', error);
      setBancos([]);
    }
  };

  // Guardar cambios del caso
  const handleSaveChanges = async () => {
    try {
      setSaving(true);
      await estafasBancariasAPI.update(id, {
        banco_nombre: editData.banco_nombre,
        banco_direccion: editData.banco_direccion,
        banco_telefono: editData.banco_telefono,
        banco_email: editData.banco_email,
        tipo_estafa: editData.tipo_estafa,
        tipo_estafa_otro: editData.tipo_estafa_otro,
        fecha_fraude: editData.fecha_fraude,
        lugar_fraude: editData.lugar_fraude,
        cantidad_estafada: parseFloat(editData.cantidad_estafada),
        numero_cuenta: editData.numero_cuenta,
        numero_tarjeta: editData.numero_tarjeta,
        descripcion_hechos: editData.descripcion_hechos,
        notas_internas: editData.notas_internas
      });
      toast.success('Cambios guardados correctamente');
      setEditMode(false);
      loadCaso();
    } catch (error) {
      console.error('Error guardando cambios:', error);
      toast.error('Error al guardar los cambios');
    } finally {
      setSaving(false);
    }
  };

  // Subir múltiples documentos
  const handleUploadDocumentos = async () => {
    if (uploadFiles.length === 0 || !uploadTipo) {
      toast.error('Seleccione al menos un archivo y tipo');
      return;
    }
    
    try {
      setUploading(true);
      
      for (const file of uploadFiles) {
        const formData = new FormData();
        formData.append('archivo', file);
        formData.append('tipo', uploadTipo);
        formData.append('paso', uploadPaso || '');
        formData.append('descripcion', uploadDescripcion);
        
        await estafasBancariasAPI.uploadDocumento(id, formData);
      }
      
      toast.success(`${uploadFiles.length} documento(s) subido(s) correctamente`);
      setShowUploadDoc(false);
      setUploadFiles([]);
      setUploadTipo('');
      setUploadDescripcion('');
      loadCaso();
    } catch (error) {
      console.error('Error subiendo documentos:', error);
      toast.error('Error al subir los documentos');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadDocumento = async (docId, nombreArchivo) => {
    try {
      const res = await estafasBancariasAPI.downloadDocumento(id, docId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nombreArchivo);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error descargando documento:', error);
      toast.error('Error al descargar el documento');
    }
  };

  const handleDeleteDocumento = async (docId) => {
    if (!confirm('¿Está seguro de eliminar este documento?')) return;
    
    try {
      await estafasBancariasAPI.deleteDocumento(id, docId);
      toast.success('Documento eliminado');
      loadCaso();
    } catch (error) {
      console.error('Error eliminando documento:', error);
      toast.error('Error al eliminar el documento');
    }
  };

  const handleGenerarDocumento = async (tipo) => {
    try {
      setGenerandoDoc(tipo);
      const res = await estafasBancariasAPI.generarDocumento(id, tipo);
      toast.success(`Documento ${tipo} generado correctamente`);
      loadCaso();
      handleDownloadDocumento(res.data.id, res.data.nombre_archivo);
    } catch (error) {
      console.error('Error generando documento:', error);
      toast.error('Error al generar el documento');
    } finally {
      setGenerandoDoc(null);
    }
  };

  const handleMarcarPasoCompletado = async (pasoId) => {
    try {
      await estafasBancariasAPI.updatePaso(id, {
        paso: pasoId,
        completado: true,
        fecha: new Date().toISOString().split('T')[0]
      });
      toast.success('Paso marcado como completado');
      loadCaso();
    } catch (error) {
      console.error('Error actualizando paso:', error);
      toast.error('Error al actualizar el paso');
    }
  };

  const handleRegistrarEnvio = async () => {
    if (!envioData.fecha || !envioData.medio) {
      toast.error('Complete todos los campos');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('fecha_envio', envioData.fecha);
      formData.append('medio_envio', envioData.medio);
      
      await estafasBancariasAPI.registrarEnvio(id, formData);
      toast.success('Envío registrado');
      setShowEnvio(false);
      loadCaso();
    } catch (error) {
      console.error('Error registrando envío:', error);
      toast.error('Error al registrar el envío');
    }
  };

  const handleRegistrarRespuesta = async () => {
    if (!respuestaData.fecha) {
      toast.error('Indique la fecha de respuesta');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('fecha_respuesta', respuestaData.fecha);
      formData.append('respuesta_favorable', respuestaData.favorable);
      if (respuestaData.importe) formData.append('importe_devuelto', respuestaData.importe);
      if (respuestaData.observaciones) formData.append('observaciones', respuestaData.observaciones);
      
      await estafasBancariasAPI.registrarRespuesta(id, formData);
      toast.success('Respuesta registrada');
      setShowRespuesta(false);
      loadCaso();
    } catch (error) {
      console.error('Error registrando respuesta:', error);
      toast.error('Error al registrar la respuesta');
    }
  };

  const handleCrearExpediente = async () => {
    try {
      const res = await estafasBancariasAPI.crearExpediente(id);
      toast.success(`Expediente ${res.data.numero_expediente} creado`);
      setShowCrearExpediente(false);
      loadCaso();
    } catch (error) {
      console.error('Error creando expediente:', error);
      toast.error(error.response?.data?.detail || 'Error al crear el expediente');
    }
  };

  // Obtener icono según extensión
  const getFileIcon = (extension) => {
    const ext = extension?.toLowerCase().replace('.', '');
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return <Image className="w-4 h-4 text-green-500" />;
    if (['pdf'].includes(ext)) return <FileText className="w-4 h-4 text-red-500" />;
    if (['doc', 'docx'].includes(ext)) return <FileText className="w-4 h-4 text-blue-500" />;
    if (['xls', 'xlsx'].includes(ext)) return <FileSpreadsheet className="w-4 h-4 text-green-600" />;
    return <File className="w-4 h-4 text-gray-500" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!caso) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500">
          <AlertCircle className="w-12 h-12 mx-auto mb-4" />
          <p>Caso no encontrado</p>
          <Button variant="link" onClick={() => navigate('/estafas-bancarias')}>
            Volver a la lista
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/estafas-bancarias')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              <h1 className="text-xl font-bold">{caso.numero_referencia}</h1>
              {caso.expediente_id && (
                <Badge variant="outline" className="text-green-600">
                  Expediente #{caso.expediente_id}
                </Badge>
              )}
            </div>
            <p className="text-gray-500 text-sm">
              {TIPOS_ESTAFA.find(t => t.id === caso.tipo_estafa)?.nombre}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {!caso.expediente_id && (
            <Button 
              variant="default" 
              className="gap-2 bg-green-600 hover:bg-green-700"
              onClick={() => setShowCrearExpediente(true)}
            >
              <FolderOpen className="w-4 h-4" />
              Crear Expediente
            </Button>
          )}
        </div>
      </div>

      {/* Barra de progreso */}
      <WorkflowProgressBar caso={caso} />

      {/* Info principal */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <User className="w-10 h-10 text-blue-500 bg-blue-50 p-2 rounded-lg" />
              <div>
                <p className="text-xs text-gray-500">Cliente</p>
                <p className="font-semibold">{caso.cliente_nombre}</p>
                <p className="text-xs text-gray-500">{caso.cliente_dni}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Building2 className="w-10 h-10 text-orange-500 bg-orange-50 p-2 rounded-lg" />
              <div>
                <p className="text-xs text-gray-500">Banco (Parte Contraria)</p>
                <p className="font-semibold">{caso.banco_nombre}</p>
                {caso.banco_direccion && (
                  <p className="text-xs text-gray-500 truncate max-w-[150px]" title={caso.banco_direccion}>
                    {caso.banco_direccion}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Euro className="w-10 h-10 text-red-500 bg-red-50 p-2 rounded-lg" />
              <div>
                <p className="text-xs text-gray-500">Cantidad Estafada</p>
                <p className="font-bold text-xl text-red-600">{formatCurrency(caso.cantidad_estafada)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-10 h-10 text-purple-500 bg-purple-50 p-2 rounded-lg" />
              <div>
                <p className="text-xs text-gray-500">Fecha Fraude</p>
                <p className="font-semibold">
                  {caso.fecha_fraude ? new Date(caso.fecha_fraude).toLocaleDateString('es-ES') : '-'}
                </p>
                <p className="text-xs text-gray-500">{caso.lugar_fraude}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="flujo" className="gap-2">
            <CheckCircle className="w-4 h-4" />
            Flujo de Trabajo
          </TabsTrigger>
          <TabsTrigger value="documentos" className="gap-2">
            <FileText className="w-4 h-4" />
            Documentos ({caso.documentos?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="datos" className="gap-2">
            <Settings className="w-4 h-4" />
            Datos del Caso
          </TabsTrigger>
          <TabsTrigger value="historial" className="gap-2">
            <History className="w-4 h-4" />
            Historial
          </TabsTrigger>
        </TabsList>

        {/* Tab: Flujo de Trabajo */}
        <TabsContent value="flujo" className="mt-4">
          <Card>
            <CardContent className="p-6">
              <div className="space-y-4">
                {PASOS_CONFIG.slice(0, -1).map((paso, index) => {
                  const completado = isPasoCompletado(paso.id);
                  const documentos = getDocumentosPaso(paso.id);
                  const PasoIcon = paso.icon;
                  
                  return (
                    <div key={paso.id} className="border rounded-lg">
                      <div 
                        className={`p-4 flex items-center justify-between ${
                          completado ? 'bg-green-50' : 'bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            completado ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
                          }`}>
                            {completado ? (
                              <CheckCircle className="w-5 h-5" />
                            ) : (
                              <span className="font-bold text-sm">{paso.letra}</span>
                            )}
                          </div>
                          <div>
                            <p className="font-semibold">{paso.nombre}</p>
                            <p className="text-sm text-gray-500">{paso.descripcion}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {documentos.length > 0 && (
                            <Badge variant="outline" className="gap-1">
                              <FileText className="w-3 h-3" />
                              {documentos.length}
                            </Badge>
                          )}
                          {paso.generado ? (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleGenerarDocumento(paso.tipoGeneracion)}
                              disabled={generandoDoc === paso.tipoGeneracion}
                            >
                              {generandoDoc === paso.tipoGeneracion ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <FileText className="w-4 h-4 mr-1" />
                                  Generar PDF
                                </>
                              )}
                            </Button>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setUploadPaso(paso.id);
                                setUploadTipo(paso.tiposDoc[0] || '');
                                setShowUploadDoc(true);
                              }}
                            >
                              <Upload className="w-4 h-4 mr-1" />
                              Subir
                            </Button>
                          )}
                          {!completado && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-green-600"
                              onClick={() => handleMarcarPasoCompletado(paso.id)}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Completar
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {/* Lista de documentos del paso con vista previa */}
                      {documentos.length > 0 && (
                        <div className="p-4 border-t bg-white">
                          <div className="space-y-2">
                            {documentos.map(doc => (
                              <div key={doc.id} className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors">
                                <div className="flex items-center gap-2">
                                  {getFileIcon(doc.extension)}
                                  <span className="text-sm">{doc.nombre_original || doc.nombre_archivo}</span>
                                  {doc.es_generado && (
                                    <Badge variant="outline" className="text-xs">Generado</Badge>
                                  )}
                                </div>
                                <div className="flex gap-1">
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => setPreviewDoc({...doc, caso_id: id})}
                                    title="Vista previa"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => handleDownloadDocumento(doc.id, doc.nombre_original || doc.nombre_archivo)}
                                    title="Descargar"
                                  >
                                    <Download className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="text-red-500"
                                    onClick={() => handleDeleteDocumento(doc.id)}
                                    title="Eliminar"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Paso especial: Envío al Banco */}
                <div className="border rounded-lg">
                  <div className={`p-4 flex items-center justify-between ${
                    caso.fecha_envio_banco ? 'bg-green-50' : 'bg-gray-50'
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        caso.fecha_envio_banco ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
                      }`}>
                        {caso.fecha_envio_banco ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <span className="font-bold text-sm">G</span>
                        )}
                      </div>
                      <div>
                        <p className="font-semibold">G) Envío al Banco</p>
                        {caso.fecha_envio_banco ? (
                          <p className="text-sm text-gray-500">
                            Enviado el {new Date(caso.fecha_envio_banco).toLocaleDateString('es-ES')} por {caso.medio_envio}
                          </p>
                        ) : (
                          <p className="text-sm text-gray-500">Registrar envío de la reclamación</p>
                        )}
                      </div>
                    </div>
                    {!caso.fecha_envio_banco && (
                      <Button size="sm" onClick={() => setShowEnvio(true)}>
                        <Send className="w-4 h-4 mr-1" />
                        Registrar Envío
                      </Button>
                    )}
                  </div>
                </div>

                {/* Paso especial: Respuesta del Banco */}
                {caso.fecha_envio_banco && (
                  <div className="border rounded-lg">
                    <div className={`p-4 flex items-center justify-between ${
                      caso.fecha_respuesta_banco ? 'bg-green-50' : 'bg-yellow-50'
                    }`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          caso.fecha_respuesta_banco 
                            ? caso.respuesta_favorable ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                            : 'bg-yellow-500 text-white'
                        }`}>
                          {caso.fecha_respuesta_banco ? (
                            caso.respuesta_favorable ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />
                          ) : (
                            <Clock className="w-5 h-5" />
                          )}
                        </div>
                        <div>
                          <p className="font-semibold">Respuesta del Banco</p>
                          {caso.fecha_respuesta_banco ? (
                            <div className="text-sm">
                              <p className={caso.respuesta_favorable ? 'text-green-600' : 'text-red-600'}>
                                {caso.respuesta_favorable ? 'Favorable' : 'Desfavorable'} - {new Date(caso.fecha_respuesta_banco).toLocaleDateString('es-ES')}
                              </p>
                              {caso.importe_devuelto > 0 && (
                                <p className="text-green-600">Importe devuelto: {formatCurrency(caso.importe_devuelto)}</p>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm text-yellow-600">Pendiente de respuesta</p>
                          )}
                        </div>
                      </div>
                      {!caso.fecha_respuesta_banco && (
                        <Button size="sm" onClick={() => setShowRespuesta(true)}>
                          <MessageSquare className="w-4 h-4 mr-1" />
                          Registrar Respuesta
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Paso final: Crear Expediente */}
                <div className="border-2 border-dashed rounded-lg">
                  <div className={`p-4 flex items-center justify-between ${
                    caso.expediente_id ? 'bg-green-50 border-green-300' : 'bg-gray-50'
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        caso.expediente_id ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
                      }`}>
                        {caso.expediente_id ? <CheckCircle className="w-5 h-5" /> : <span className="font-bold text-sm">H</span>}
                      </div>
                      <div>
                        <p className="font-semibold">H) Crear Expediente Judicial</p>
                        {caso.expediente_id ? (
                          <p className="text-sm text-green-600">
                            Expediente #{caso.expediente_id} creado el {new Date(caso.fecha_conversion).toLocaleDateString('es-ES')}
                          </p>
                        ) : (
                          <p className="text-sm text-gray-500">Convertir el caso en expediente judicial</p>
                        )}
                      </div>
                    </div>
                    {caso.expediente_id ? (
                      <Button 
                        variant="outline" 
                        onClick={() => navigate(`/expedientes/${caso.expediente_id}`)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Ver Expediente
                      </Button>
                    ) : (
                      <Button 
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => setShowCrearExpediente(true)}
                      >
                        <FolderOpen className="w-4 h-4 mr-1" />
                        Crear Expediente
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Documentos */}
        <TabsContent value="documentos" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Documentos del Caso</CardTitle>
              <Button size="sm" onClick={() => {
                setUploadPaso(null);
                setShowUploadDoc(true);
              }}>
                <Plus className="w-4 h-4 mr-1" />
                Subir Documentos
              </Button>
            </CardHeader>
            <CardContent>
              {caso.documentos?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No hay documentos adjuntos</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {caso.documentos?.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="flex items-center gap-3">
                        {getFileIcon(doc.extension)}
                        <div>
                          <p className="font-medium text-sm">{doc.nombre_original || doc.nombre_archivo}</p>
                          <div className="flex gap-2 text-xs text-gray-500">
                            <span>{TIPOS_DOCUMENTO.find(t => t.id === doc.tipo)?.nombre || doc.tipo}</span>
                            {doc.es_generado && <Badge variant="outline" className="text-xs">Generado</Badge>}
                            <span>• {new Date(doc.created_at).toLocaleDateString('es-ES')}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setPreviewDoc({...doc, caso_id: id})}
                          title="Vista previa"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDownloadDocumento(doc.id, doc.nombre_original || doc.nombre_archivo)}
                          title="Descargar"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-red-500"
                          onClick={() => handleDeleteDocumento(doc.id)}
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Datos del Caso */}
        <TabsContent value="datos" className="mt-4">
          <div className="flex justify-end mb-4">
            {editMode ? (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setEditMode(false); setEditData(caso); }}>
                  <X className="w-4 h-4 mr-1" />
                  Cancelar
                </Button>
                <Button onClick={handleSaveChanges} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Guardar Cambios
                </Button>
              </div>
            ) : (
              <Button onClick={() => setEditMode(true)}>
                <Edit className="w-4 h-4 mr-1" />
                Editar Datos
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Datos del Cliente */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Datos del Cliente
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs text-gray-500">Nombre</Label>
                  <p className="font-medium">{caso.cliente_nombre}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">DNI/NIF</Label>
                  <p>{caso.cliente_dni || '-'}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Dirección</Label>
                  <p>{caso.cliente_direccion || '-'}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Teléfono</Label>
                  <p>{caso.cliente_telefono || '-'}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Email</Label>
                  <p>{caso.cliente_email || '-'}</p>
                </div>
              </CardContent>
            </Card>

            {/* Datos del Banco (Parte Contraria) - MEJORADO */}
            <Card className="border-orange-200">
              <CardHeader className="bg-orange-50">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-orange-600" />
                  Banco (Parte Contraria)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 pt-4">
                {editMode ? (
                  <>
                    <div className="space-y-2">
                      <Label>Nombre del Banco *</Label>
                      <Input
                        value={editData.banco_nombre || ''}
                        onChange={(e) => {
                          setEditData({...editData, banco_nombre: e.target.value});
                          setSearchBanco(e.target.value);
                          searchBancos(e.target.value);
                        }}
                        placeholder="Nombre de la entidad bancaria"
                      />
                      {bancos.length > 0 && searchBanco && (
                        <div className="border rounded-md max-h-32 overflow-y-auto bg-white shadow-sm">
                          {bancos.map((b) => (
                            <div
                              key={b.id}
                              className="p-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                              onClick={() => {
                                setEditData({
                                  ...editData,
                                  banco_id: b.id,
                                  banco_nombre: b.nombre,
                                  banco_direccion: b.direccion,
                                  banco_telefono: b.telefono,
                                  banco_email: b.email
                                });
                                setSearchBanco('');
                                setBancos([]);
                              }}
                            >
                              <p className="font-medium">{b.nombre}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Dirección</Label>
                      <Input
                        value={editData.banco_direccion || ''}
                        onChange={(e) => setEditData({...editData, banco_direccion: e.target.value})}
                        placeholder="Dirección de la sucursal"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Teléfono</Label>
                      <Input
                        value={editData.banco_telefono || ''}
                        onChange={(e) => setEditData({...editData, banco_telefono: e.target.value})}
                        placeholder="Teléfono de contacto"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        value={editData.banco_email || ''}
                        onChange={(e) => setEditData({...editData, banco_email: e.target.value})}
                        placeholder="Email de contacto"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Label className="text-xs text-gray-500">Nombre</Label>
                      <p className="font-medium text-lg">{caso.banco_nombre}</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-gray-400 mt-1" />
                      <div>
                        <Label className="text-xs text-gray-500">Dirección</Label>
                        <p>{caso.banco_direccion || 'No especificada'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Phone className="w-4 h-4 text-gray-400 mt-1" />
                      <div>
                        <Label className="text-xs text-gray-500">Teléfono</Label>
                        <p>{caso.banco_telefono || 'No especificado'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Mail className="w-4 h-4 text-gray-400 mt-1" />
                      <div>
                        <Label className="text-xs text-gray-500">Email</Label>
                        <p>{caso.banco_email || 'No especificado'}</p>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Datos del Fraude */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Datos del Fraude
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {editMode ? (
                  <>
                    <div className="space-y-2">
                      <Label>Tipo de Estafa *</Label>
                      <Select
                        value={editData.tipo_estafa}
                        onValueChange={(val) => setEditData({...editData, tipo_estafa: val})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIPOS_ESTAFA.map((tipo) => (
                            <SelectItem key={tipo.id} value={tipo.id}>{tipo.nombre}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {editData.tipo_estafa === 'otro' && (
                      <div className="space-y-2">
                        <Label>Especificar tipo</Label>
                        <Input
                          value={editData.tipo_estafa_otro || ''}
                          onChange={(e) => setEditData({...editData, tipo_estafa_otro: e.target.value})}
                        />
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-2">
                        <Label>Fecha del Fraude *</Label>
                        <Input
                          type="date"
                          value={editData.fecha_fraude?.split('T')[0] || ''}
                          onChange={(e) => setEditData({...editData, fecha_fraude: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Cantidad (€) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editData.cantidad_estafada || ''}
                          onChange={(e) => setEditData({...editData, cantidad_estafada: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Lugar del Fraude *</Label>
                      <Input
                        value={editData.lugar_fraude || ''}
                        onChange={(e) => setEditData({...editData, lugar_fraude: e.target.value})}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-2">
                        <Label>Nº Cuenta</Label>
                        <Input
                          value={editData.numero_cuenta || ''}
                          onChange={(e) => setEditData({...editData, numero_cuenta: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Nº Tarjeta</Label>
                        <Input
                          value={editData.numero_tarjeta || ''}
                          onChange={(e) => setEditData({...editData, numero_tarjeta: e.target.value})}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Label className="text-xs text-gray-500">Tipo de Estafa</Label>
                      <p className="font-medium">{TIPOS_ESTAFA.find(t => t.id === caso.tipo_estafa)?.nombre}</p>
                      {caso.tipo_estafa_otro && <p className="text-sm text-gray-500">{caso.tipo_estafa_otro}</p>}
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Fecha del Fraude</Label>
                      <p>{caso.fecha_fraude ? new Date(caso.fecha_fraude).toLocaleDateString('es-ES') : '-'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Lugar</Label>
                      <p>{caso.lugar_fraude}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Cantidad Estafada</Label>
                      <p className="font-bold text-red-600 text-lg">{formatCurrency(caso.cantidad_estafada)}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-gray-500">Nº Cuenta</Label>
                        <p className="font-mono text-sm">{caso.numero_cuenta || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Nº Tarjeta</Label>
                        <p className="font-mono text-sm">{caso.numero_tarjeta || '-'}</p>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Descripción de los Hechos */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Descripción de los Hechos</CardTitle>
              </CardHeader>
              <CardContent>
                {editMode ? (
                  <Textarea
                    rows={6}
                    value={editData.descripcion_hechos || ''}
                    onChange={(e) => setEditData({...editData, descripcion_hechos: e.target.value})}
                    placeholder="Describa cómo ocurrió la estafa..."
                  />
                ) : (
                  <p className="text-gray-700 whitespace-pre-wrap">
                    {caso.descripcion_hechos || 'Sin descripción'}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab: Historial */}
        <TabsContent value="historial" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Historial de Acciones
              </CardTitle>
            </CardHeader>
            <CardContent>
              {caso.historial?.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No hay historial</p>
              ) : (
                <div className="space-y-3">
                  {caso.historial?.map(h => (
                    <div key={h.id} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                      <div className="flex-1">
                        <p className="font-medium">{h.descripcion}</p>
                        <div className="flex gap-4 text-xs text-gray-500 mt-1">
                          <span>{new Date(h.created_at).toLocaleString('es-ES')}</span>
                          {h.usuario_nombre && <span>Por: {h.usuario_nombre}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal: Subir Documentos (MÚLTIPLES) */}
      <Dialog open={showUploadDoc} onOpenChange={setShowUploadDoc}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Subir Documentos</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Tipo de Documento</Label>
              <Select value={uploadTipo} onValueChange={setUploadTipo}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar tipo..." />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_DOCUMENTO.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Archivos (puede seleccionar múltiples)</Label>
              <Input
                type="file"
                multiple
                onChange={(e) => setUploadFiles(Array.from(e.target.files))}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.xls,.xlsx"
              />
              {uploadFiles.length > 0 && (
                <div className="mt-2 space-y-1">
                  {uploadFiles.map((file, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                      {getFileIcon(file.name.split('.').pop())}
                      <span>{file.name}</span>
                      <span className="text-xs text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Descripción (opcional)</Label>
              <Textarea
                value={uploadDescripcion}
                onChange={(e) => setUploadDescripcion(e.target.value)}
                placeholder="Descripción de los documentos..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDoc(false)}>Cancelar</Button>
            <Button onClick={handleUploadDocumentos} disabled={uploading}>
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Subiendo...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Subir {uploadFiles.length > 0 && `(${uploadFiles.length})`}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Registrar Envío */}
      <Dialog open={showEnvio} onOpenChange={setShowEnvio}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Envío al Banco</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Fecha de Envío</Label>
              <Input
                type="date"
                value={envioData.fecha}
                onChange={(e) => setEnvioData({...envioData, fecha: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Medio de Envío</Label>
              <Select value={envioData.medio} onValueChange={(v) => setEnvioData({...envioData, medio: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar medio..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="burofax">Burofax</SelectItem>
                  <SelectItem value="correo_certificado">Correo Certificado</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="presencial">Presencial</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEnvio(false)}>Cancelar</Button>
            <Button onClick={handleRegistrarEnvio}>Registrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Registrar Respuesta */}
      <Dialog open={showRespuesta} onOpenChange={setShowRespuesta}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Respuesta del Banco</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Fecha de Respuesta</Label>
              <Input
                type="date"
                value={respuestaData.fecha}
                onChange={(e) => setRespuestaData({...respuestaData, fecha: e.target.value})}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="favorable"
                checked={respuestaData.favorable}
                onChange={(e) => setRespuestaData({...respuestaData, favorable: e.target.checked})}
              />
              <Label htmlFor="favorable">Respuesta Favorable</Label>
            </div>
            {respuestaData.favorable && (
              <div className="space-y-2">
                <Label>Importe Devuelto (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={respuestaData.importe}
                  onChange={(e) => setRespuestaData({...respuestaData, importe: e.target.value})}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Observaciones</Label>
              <Textarea
                value={respuestaData.observaciones}
                onChange={(e) => setRespuestaData({...respuestaData, observaciones: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRespuesta(false)}>Cancelar</Button>
            <Button onClick={handleRegistrarRespuesta}>Registrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Crear Expediente */}
      <AlertDialog open={showCrearExpediente} onOpenChange={setShowCrearExpediente}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Crear Expediente Judicial</AlertDialogTitle>
            <AlertDialogDescription>
              Se creará un nuevo expediente con todos los datos y documentos de este caso.
              <br /><br />
              <strong>Tipo:</strong> Civil<br />
              <strong>Materia:</strong> Estafa Bancaria<br />
              <strong>Cliente:</strong> {caso.cliente_nombre}<br />
              <strong>Cantidad:</strong> {formatCurrency(caso.cantidad_estafada)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleCrearExpediente}>
              Crear Expediente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal: Vista Previa de Documento */}
      {previewDoc && (
        <DocumentPreview 
          documento={previewDoc} 
          onClose={() => setPreviewDoc(null)} 
        />
      )}
    </div>
  );
};

export default EstafaBancariaDetalle;
